import ExcelJS from 'exceljs/dist/exceljs.min.js';

import { AttributionRole } from '../@types/CoproUserAttribution';
import { JWTHeader } from './JWTHeader';

type UserInfo = { nom: string; prenom: string };

interface RnicApiItem {
  numeroDimmatriculation?: string;
  nomDusageDeLaCopropriete?: string;
  numeroEtVoieAdresseDeReference?: string;
  codePostalAdresseDeReference?: string;
  communeAdresseDeReference?: string;
  adresseDeReference?: string;
  detailsInforattachementDatedebutmandat?: string;
  dateDeFinDuDernierMandat?: string;
  detailsDonneesfinancieresDateag?: string;
}

interface AttributionApiItem {
  copro?: { remoteId?: string };
  user?: { firstname?: string; lastname?: string };
  fonctionType: string;
}

// Format date as French dd/mm/yyyy (specific to the Excel export — kept local).
const formatDateFR = (dateString: string | null | undefined): string => {
  if (!dateString) {return '';}
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {return '';}
    return date.toLocaleDateString('fr-FR');
  } catch {
    return '';
  }
};

export class CoproRnicExcelExportEmptyError extends Error {
  constructor() {
    super('Aucune copropriété RNIC à exporter.');
    this.name = 'CoproRnicExcelExportEmptyError';
  }
}

export const exportCoprosRnicToExcel = async(
  companyId: number | string,
  companySiren: string,
  jwt: string,
): Promise<void> => {
  const [rnicResponse, coprosResponse, attributionsResponse] = await Promise.all([
    fetch(`/api/companies/${companyId}/rnics`, JWTHeader(jwt)),
    fetch(`/api/companies/${companyId}/copros?limit=100000`, JWTHeader(jwt)),
    fetch(`/api/companies/${companyId}/validated-copros/attributions`, JWTHeader(jwt)),
  ]);

  if (!rnicResponse.ok) {
    throw new Error(`HTTP error status: ${rnicResponse.status}`);
  }
  const rnicData: RnicApiItem[] = await rnicResponse.json();

  if (!coprosResponse.ok) {
    throw new Error(`HTTP error status: ${coprosResponse.status}`);
  }
  const coprosData = (await coprosResponse.json()).results as { remoteId?: string; validatedByUser?: boolean }[];

  const attributionsByRemoteId: Record<string, {
    gestionnaire: UserInfo[];
    transactionnaire: UserInfo[];
    gestionnaireLocatif: UserInfo[];
  }> = {};

  if (attributionsResponse.ok) {
    const attributionsData = await attributionsResponse.json() as AttributionApiItem[];
    attributionsData.forEach((attr) => {
      const remoteId = attr.copro?.remoteId;
      if (!remoteId) {return;}
      if (!attributionsByRemoteId[remoteId]) {
        attributionsByRemoteId[remoteId] = { gestionnaire: [], transactionnaire: [], gestionnaireLocatif: [] };
      }
      const userInfo: UserInfo = { nom: attr.user?.lastname || '', prenom: attr.user?.firstname || '' };
      if (attr.fonctionType === AttributionRole.GESTIONNAIRE) {
        attributionsByRemoteId[remoteId].gestionnaire.push(userInfo);
      } else if (attr.fonctionType === AttributionRole.TRANSACTIONNAIRE) {
        attributionsByRemoteId[remoteId].transactionnaire.push(userInfo);
      } else if (attr.fonctionType === AttributionRole.GESTIONNAIRE_LOCATIF) {
        attributionsByRemoteId[remoteId].gestionnaireLocatif.push(userInfo);
      }
    });
  }

  if (rnicData.length === 0) {
    throw new CoproRnicExcelExportEmptyError();
  }

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Copropriétés RNIC');

  const headerStyle = {
    font: { bold: true, color: { argb: 'FFFFFFFF' } },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    border: {
      top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
    },
  };

  const normalCellStyle = {
    alignment: { horizontal: 'left', vertical: 'middle' },
    border: {
      top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' },
    },
  };

  const emptyCellFill = {
    type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFA0' },
  };

  const highlightFill = {
    type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF92D050' },
  };

  const headers = [
    'Copro validée',
    'Type d\'immeuble (copro, ASL, AFUL, tertiaire, parking)',
    'Identifiant RNIC',
    'Nom',
    'Adresse',
    'Code postal',
    'Ville',
    'Adresse brute',
    'Date de début de mandat',
    'Date de fin de mandat',
    'Date AG',
    'Copro toujours active',
    'Nom du gestionnaire de la copro',
    'Prénom du gestionnaire de la copro',
    'Nom transactionnaire copro',
    'Prénom transactionnaire copro',
    'Nom gestionnaire locatif copro',
    'Prénom gestionnaire locatif copro',
  ];
  worksheet.addRow(headers).eachCell((cell) => Object.assign(cell, headerStyle));

  rnicData.forEach((rnic) => {
    const copro = coprosData.find((c) => c.remoteId === rnic.numeroDimmatriculation);
    const attrs = (rnic.numeroDimmatriculation && attributionsByRemoteId[rnic.numeroDimmatriculation])
      || { gestionnaire: [], transactionnaire: [], gestionnaireLocatif: [] };
    const joinField = (list: UserInfo[], field: 'nom' | 'prenom') => list.map(u => u[field]).filter(Boolean).join(' / ');
    const row = worksheet.addRow([
      copro?.validatedByUser ? 'Oui' : 'Non',
      'Copropriété',
      rnic.numeroDimmatriculation || '',
      rnic.nomDusageDeLaCopropriete || '',
      rnic.numeroEtVoieAdresseDeReference || '',
      rnic.codePostalAdresseDeReference || '',
      rnic.communeAdresseDeReference || '',
      rnic.adresseDeReference || '',
      formatDateFR(rnic.detailsInforattachementDatedebutmandat),
      formatDateFR(rnic.dateDeFinDuDernierMandat),
      formatDateFR(rnic.detailsDonneesfinancieresDateag),
      'OUI',
      joinField(attrs.gestionnaire, 'nom'),
      joinField(attrs.gestionnaire, 'prenom'),
      joinField(attrs.transactionnaire, 'nom'),
      joinField(attrs.transactionnaire, 'prenom'),
      joinField(attrs.gestionnaireLocatif, 'nom'),
      joinField(attrs.gestionnaireLocatif, 'prenom'),
    ]);
    row.eachCell((cell, colNumber) => {
      Object.assign(cell, normalCellStyle);
      if (colNumber === 12) {
        Object.assign(cell, { fill: highlightFill });
      } else if (!cell.value || cell.value === '') {
        Object.assign(cell, { fill: emptyCellFill });
      }
    });
  });

  worksheet.columns.forEach((column, index) => {
    const widths = [
      15, 45, 20, 40, 40, 12, 25, 50, 22, 22, 15, 20, 35, 35, 30, 30, 30, 30,
    ];
    column.width = widths[index] || 20;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${companySiren}-coproprietes-rnic.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
};
